import { useState } from "react";
import {
  Briefcase,
  FlaskConical,
  GraduationCap,
  Stethoscope,
  Users,
  Wrench,
} from "lucide-react";
import { Button, Chip, Section, SectionHeading, StickerCard } from "@/components/ui";
import { EXPERIENCES } from "@/data/home";

function getExperienceIcon(id) {
  switch (id) {
    case "conferences":
      return Users;
    case "masterclasses":
      return GraduationCap;
    case "internships":
      return Briefcase;
    case "skill-development":
      return Wrench;
    case "observerships":
      return Stethoscope;
    case "research":
      return FlaskConical;
    default:
      return Briefcase;
  }
}

/** Alternate blue / orange accents. Full literals for Tailwind. */
const ACCENTS = [
  { tone: "white", badge: "bg-brand-100 text-ink" },
  { tone: "white", badge: "bg-brand-100 text-ink" },
];

export function ExperiencesSection() {
  const [filter, setFilter] = useState("all");
  const { more } = EXPERIENCES;

  return (
    <Section id="experiences" labelledBy="experiences-title" testId="home-experiences" tone="brandTint">
      <SectionHeading id="experiences-title" title={EXPERIENCES.title} highlight="Take your interest further." intro={EXPERIENCES.intro} />

      <div className="mt-6 flex flex-wrap gap-2.5 sm:gap-3" role="group" aria-label="Filter experiences">
        {EXPERIENCES.filters.map((item) => (
          <Chip
            key={item.id}
            pressed={filter === item.id}
            onClick={() => setFilter(item.id)}
            data-testid={`experience-filter-${item.id}`}
          >
            {item.label}
          </Chip>
        ))}
      </div>

      <ul className="mt-8 grid gap-3 sm:gap-4 md:grid-cols-3" data-testid="experience-grid">
        {EXPERIENCES.items.map((item, i) => {
          const Icon = getExperienceIcon(item.id);
          const accent = ACCENTS[i % ACCENTS.length];
          const dimmed = filter !== "all" && !item.tags.includes(filter);
          return (
            <li key={item.id} className={`transition-opacity duration-300 ${dimmed ? "opacity-30" : "opacity-100"}`}>
              <StickerCard tone={accent.tone} className="flex h-full flex-col gap-3" data-testid={`experience-${item.id}`}>
                <span className={`inline-flex h-11 w-11 items-center justify-center rounded-full ${accent.badge}`}>
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="font-heading text-xl font-bold leading-tight">{item.title}</h3>
                <p className="text-sm text-ink/70 sm:text-base">{item.text}</p>
                <p className="mt-auto rounded-2xl bg-brand-50 px-3 py-2 text-sm font-semibold">
                  <span className="text-ink/70">You take away:</span> {item.takeaway}
                </p>
              </StickerCard>
            </li>
          );
        })}

        <li className="md:col-span-3">
          <StickerCard tone="sun" wiggle={false} className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-heading text-2xl font-bold">{more.title}</h3>
              <p className="mt-1 max-w-[60ch] text-base">{more.text}</p>
            </div>
            <Button to={more.cta.to} variant="outline" className="shrink-0">
              {more.cta.label}
            </Button>
          </StickerCard>
        </li>
      </ul>
    </Section>
  );
}
